/**
 * ErrorOverlay — Mobile-friendly error console.
 * Shows a small bug icon; tap to see captured errors + copy to clipboard.
 * Captures: console.error, console.warn, window.onerror, unhandledrejection.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

interface LogEntry {
    type: 'error' | 'warn';
    message: string;
    timestamp: number;
}

const MAX_ENTRIES = 50;

// Module-level log buffer so entries survive re-renders
const _logs: LogEntry[] = [];
let _onUpdate: (() => void) | null = null;

function pushLog(type: 'error' | 'warn', ...args: any[]) {
    const message = args.map(a => {
        if (a instanceof Error) return `${a.message}\n${a.stack ?? ''}`;
        if (typeof a === 'object') try { return JSON.stringify(a, null, 1); } catch { return String(a); }
        return String(a);
    }).join(' ');

    _logs.push({ type, message, timestamp: Date.now() });
    if (_logs.length > MAX_ENTRIES) _logs.shift();
    _onUpdate?.();
}

// Install hooks once
let _installed = false;
function installHooks() {
    if (_installed) return;
    _installed = true;

    const origError = console.error;
    const origWarn = console.warn;

    console.error = (...args: any[]) => { pushLog('error', ...args); origError.apply(console, args); };
    console.warn = (...args: any[]) => { pushLog('warn', ...args); origWarn.apply(console, args); };

    window.addEventListener('error', (e) => {
        pushLog('error', e.message, e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : '');
    });

    window.addEventListener('unhandledrejection', (e) => {
        pushLog('error', 'Unhandled Promise:', e.reason);
    });
}

const ErrorOverlay: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [, forceUpdate] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        installHooks();
        _onUpdate = () => forceUpdate(n => n + 1);
        return () => { _onUpdate = null; };
    }, []);

    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [open, _logs.length]);

    const copyAll = useCallback(async () => {
        const text = _logs.map(l => {
            const time = new Date(l.timestamp).toLocaleTimeString();
            return `[${time}] ${l.type.toUpperCase()}: ${l.message}`;
        }).join('\n\n');
        try {
            await navigator.clipboard.writeText(text);
            alert('Copied to clipboard');
        } catch {
            // Fallback for mobile
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            alert('Copied to clipboard');
        }
    }, []);

    const clearAll = useCallback(() => {
        _logs.length = 0;
        forceUpdate(n => n + 1);
    }, []);

    const errorCount = _logs.filter(l => l.type === 'error').length;

    return (
        <>
            {/* Bug icon button */}
            <button
                onClick={() => setOpen(!open)}
                style={{
                    position: 'fixed', bottom: '12px', right: '12px', zIndex: 99999,
                    width: '32px', height: '32px', borderRadius: '50%',
                    background: errorCount > 0 ? 'rgba(239,68,68,0.6)' : 'rgba(100,116,139,0.4)',
                    border: `1px solid ${errorCount > 0 ? 'rgba(239,68,68,0.8)' : 'rgba(100,116,139,0.5)'}`,
                    color: '#f1f5f9', fontSize: '14px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(4px)', boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                }}
            >
                {errorCount > 0 ? errorCount : '\u2699'}
            </button>

            {/* Error panel */}
            {open && (
                <div style={{
                    position: 'fixed', bottom: '52px', right: '8px', zIndex: 99999,
                    width: 'min(90vw, 380px)', maxHeight: '50vh',
                    background: 'rgba(8,10,20,0.97)', border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: '10px', overflow: 'hidden',
                    fontFamily: 'monospace', fontSize: '11px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                    display: 'flex', flexDirection: 'column',
                }}>
                    {/* Header */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.1)',
                        background: 'rgba(15,23,42,0.8)',
                    }}>
                        <span style={{ color: '#94a3b8', fontWeight: 600 }}>
                            Console ({_logs.length})
                        </span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <button onClick={copyAll} style={{
                                background: 'rgba(59,130,246,0.3)', border: '1px solid rgba(59,130,246,0.5)',
                                color: '#e2e8f0', borderRadius: '4px', padding: '3px 8px',
                                fontSize: '10px', cursor: 'pointer',
                            }}>Copy All</button>
                            <button onClick={clearAll} style={{
                                background: 'rgba(100,116,139,0.3)', border: '1px solid rgba(100,116,139,0.5)',
                                color: '#e2e8f0', borderRadius: '4px', padding: '3px 8px',
                                fontSize: '10px', cursor: 'pointer',
                            }}>Clear</button>
                        </div>
                    </div>

                    {/* Log entries */}
                    <div ref={scrollRef} style={{
                        overflowY: 'auto', padding: '6px',
                        flex: 1, maxHeight: 'calc(50vh - 40px)',
                    }}>
                        {_logs.length === 0 && (
                            <div style={{ color: '#475569', padding: '12px', textAlign: 'center' }}>
                                No errors captured
                            </div>
                        )}
                        {_logs.map((log, i) => (
                            <div key={i} style={{
                                padding: '4px 6px', marginBottom: '3px', borderRadius: '4px',
                                background: log.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(234,179,8,0.08)',
                                borderLeft: `2px solid ${log.type === 'error' ? '#ef4444' : '#eab308'}`,
                                color: log.type === 'error' ? '#fca5a5' : '#fde68a',
                                wordBreak: 'break-all', whiteSpace: 'pre-wrap',
                                lineHeight: 1.4,
                            }}>
                                <span style={{ color: '#64748b', fontSize: '9px' }}>
                                    {new Date(log.timestamp).toLocaleTimeString()}
                                </span>
                                {' '}{log.message}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default ErrorOverlay;
